// The dates of the chosen event on its plate (um/2026-10-30/04 п.2): «14–15 мая 2027», «30 мая — 2 июня 2027», across years in full.

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const parts = (v: unknown): [number, number, number] | null => {
  const m = typeof v === 'string' ? /^(\d{4})-(\d{2})-(\d{2})/.exec(v) : null;
  return m ? [Number(m[1]), Number(m[2]) - 1, Number(m[3])] : null;
};

/** The span of an event in words; empty when the start is unknown. A missing or earlier end is a one-day event. */
export function eventDates(start: unknown, end: unknown): string {
  const a = parts(start);
  if (!a) return '';
  const b = parts(end);
  const [y1, m1, d1] = a;
  if (!b || String(end) <= String(start) || (b[0] === y1 && b[1] === m1 && b[2] === d1)) return `${d1} ${MONTHS[m1]} ${y1}`;
  const [y2, m2, d2] = b;
  if (y1 !== y2) return `${d1} ${MONTHS[m1]} ${y1} — ${d2} ${MONTHS[m2]} ${y2}`;
  if (m1 !== m2) return `${d1} ${MONTHS[m1]} — ${d2} ${MONTHS[m2]} ${y1}`;
  return `${d1}–${d2} ${MONTHS[m1]} ${y1}`;
}

/** The fields of the scope type that say when a record is (the header's `scope`). */
export interface ScopeDates { dateField?: string; endField?: string }
type Rec = { id: string; createdAt: string; data: Record<string, unknown> };

/** The day a scope record counts by: its end when it has one, else its start — one rule for the plate, the default choice and `$today`. */
export function scopeDay(r: Pick<Rec, 'data'>, scope: ScopeDates): string {
  const end = scope.endField ? String(r.data[scope.endField] ?? '') : '';
  return end || String(r.data[scope.dateField ?? ''] ?? '');
}

/** 0 — coming (today and later), 1 — no date, 2 — past; without a date field everything is one group. */
export function scopeGroup(r: Pick<Rec, 'data'>, scope: ScopeDates, today: string): 0 | 1 | 2 {
  const d = scopeDay(r, scope);
  return !scope.dateField ? 0 : !d ? 1 : d >= today ? 0 : 2;
}

/** The records for the choice of the event: the coming ones by their day, the nearest first, then without a date, then the past, the latest first. */
export function scopeOrder<R extends Rec>(recs: R[], scope: ScopeDates, today: string): { r: R; g: 0 | 1 | 2 }[] {
  return recs.map((r) => ({ r, g: scopeGroup(r, scope, today), d: scopeDay(r, scope) }))
    .sort((a, b) => a.g - b.g || (a.g === 0 ? a.d.localeCompare(b.d) : a.g === 2 ? b.d.localeCompare(a.d) : 0) || (a.r.createdAt < b.r.createdAt ? 1 : -1))
    .map(({ r, g }) => ({ r, g }));
}

/** The record the scope falls back to: the nearest coming one by its day, else the latest past one, else the newest created. */
export function defaultScope(recs: Rec[], scope: ScopeDates, today: string): string | null {
  let best: { id: string; key: string; future: boolean } | null = null;
  for (const r of recs) {
    const d = scopeDay(r, scope);
    const future = !!scope.dateField && !!d && d >= today;
    const key = d || r.createdAt;
    const better = !best || (future && !best.future) || (future === best.future && (future ? key < best.key : key > best.key));
    if (better) best = { id: r.id, key, future };
  }
  return best?.id ?? null;
}
